import styles from "./index.module.less";
// import InfiniteScroll from "react-infinite-scroll-component";
import useAppStore from "@/context/store";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import HBLoading from "../HBLoading";
import InfiniteScroll from "../InfiniteScroll";
import { useLogin } from "../LoginModal";
import { useLoadingLimit } from "../LoginModal/useLoginLimit";
import Masonry from "../Masonry";
import MasonryWithBanner from "../Masonry/MasonryWithBanner";
/**
 * @param {{
 *   disableMasonry?: boolean;
 *   dataSource: any[];
 *   render: (record: any, index: number) => any;
 *   onLoadMore?: (record: any) => any;
 *   children?: any;
 *   hasMore?: boolean;
 *   scrollableTarget?: any;
 *   endMessage?: any;
 *   infiniteScrollOptions?: any;
 *   masonryOptions?: any;
 *   uniqueKey: string;
 *   loginGuide?: boolean;
 *   masonryRef?: any;
 *   listStyle?: any;
 *   filter?: any;
 *   className?: string;
 *   scrollThreshold?: string | number;
 *   onLoginLimit?: () => void;
 * }} query
 * @returns {any}
 */
export default function HBList({
  disableMasonry,
  dataSource = [],
  render,
  onLoadMore,
  children,
  hasMore = true,
  scrollableTarget,
  endMessage,
  infiniteScrollOptions,
  masonryOptions,
  uniqueKey,
  loginGuide,
  masonryRef,
  listStyle = {},
  filter = null,
  className = "",
  loadingContent = null,
  firstLoadingContent = null,
  scrollThreshold = "800px",
  pageSize,
  onLoginLimit,
  isMasonryWithBanner,
}) {
  const { login } = useLogin();
  const listRef = useRef(null);
  const [dataLength, setDataLength] = useState(dataSource.length);

  const newDataSource = useMemo(() => {
    if (filter) {
      return filter(dataSource);
    }
    return dataSource;
  }, [dataSource, filter]);

  const renderList = useMemo(() => {
    const list = [];

    let index = 0;
    newDataSource.reduce((map, record) => {
      if (uniqueKey) {
        if (!map[record[uniqueKey]]) {
          list.push(render(record, index++));
          map[record[uniqueKey]] = true;
        }
      } else {
        list.push(render(record, index++));
      }

      return map;
    }, {});

    if (children) {
      list.unshift(children);
    }

    return list;
  }, [children, newDataSource, uniqueKey, render]);

  // useEffect(() => {
  //   if (dataSource.length > 0) {
  //     if (scrollableTarget) {
  //       const target =
  //         typeof scrollableTarget === "string"
  //           ? document.getElementById(scrollableTarget)
  //           : scrollableTarget;
  //       listRef.current.throttledOnScrollListener({ target });
  //     } else {
  //       listRef.current.throttledOnScrollListener();
  //     }
  //   }
  // }, [dataSource, scrollableTarget]);
  const { user, userReady } = useAppStore(({ user, userReady }) => ({
    user,
    userReady,
  }));

  const endMsg = useMemo(() => {
    if (endMessage === undefined) {
      if (loginGuide && userReady && !user) {
        return (
          <div
            className={styles.endMessage}
            data-gd-click="button_click"
            data-button-name="注册或登录"
            data-content-source="底部栏"
          >
            <span
              onClick={() =>
                login({
                  source_location: "瀑布流底部栏-注册登录",
                })
              }
            >
              注册或登录
            </span>
            可查看更多优质内容哦~
          </div>
        );
      }
      return (
        <p
          data-gd-expose="resource_loc_expose"
          data-resource-title="没有更多了"
          data-resource-type="文字"
          className={classNames([styles.noMore, "noMore"])}
        >
          没有更多了
        </p>
      );
    } else {
      return endMessage;
    }
  }, [endMessage, loginGuide, user, userReady, login]);

  // 登录访问限制
  const { limit: loginLimit } = useLoadingLimit({
    pageListData: dataSource,
    ignore: !loginGuide,
    pageSize: pageSize,
  });

  useEffect(() => {
    if (loginLimit) {
      onLoginLimit?.();
    }
  }, [loginLimit, onLoginLimit]);

  const changeDataLength = useMemoizedFn(() => {
    setDataLength(dataSource.length);
  });

  useEffect(() => {
    if (masonryRef?.current) {
      masonryRef.current.relayout();
    }
  }, [masonryOptions?.columnNum, masonryOptions?.columnSize]);

  const loaderContent = useMemo(() => {
    // 首屏loading内容加载
    if (dataSource.length === 0 && firstLoadingContent && !children) {
      return firstLoadingContent;
    }

    return (
      loadingContent ?? (
        <div className={classNames([styles.loading, "loading"])}>
          <HBLoading size="30px" />
        </div>
      )
    );
  }, [children, dataSource.length, firstLoadingContent, loadingContent]);

  return (
    <InfiniteScroll
      dataLength={disableMasonry ? dataSource.length : dataLength}
      hasMore={hasMore && !loginLimit}
      next={onLoadMore}
      className={className}
      loader={loaderContent}
      endMessage={endMsg}
      ref={listRef}
      {...(scrollableTarget ? { scrollableTarget } : null)}
      {...infiniteScrollOptions}
      scrollThreshold={scrollThreshold}
    >
      <If condition={!disableMasonry}>
        <Then>
          {isMasonryWithBanner ? (
            <MasonryWithBanner
              bricks={newDataSource}
              brickId={uniqueKey}
              render={render}
              ref={masonryRef}
              onRendered={changeDataLength}
              loadingContent={firstLoadingContent}
              {...masonryOptions}
              {...(scrollableTarget
                ? { scrollElement: scrollableTarget }
                : null)}
            >
              {children}
            </MasonryWithBanner>
          ) : (
            <Masonry
              bricks={newDataSource}
              brickId={uniqueKey}
              render={render}
              ref={masonryRef}
              onRendered={changeDataLength}
              hasMore={hasMore && !loginLimit}
              loadingContent={firstLoadingContent}
              {...masonryOptions}
              {...(scrollableTarget
                ? { scrollElement: scrollableTarget }
                : null)}
            >
              {children}
            </Masonry>
          )}
        </Then>
        <Else>
          <div className={styles.list} style={listStyle} key="not-masonry">
            {renderList}
          </div>
        </Else>
      </If>
    </InfiniteScroll>
  );
}
